import { forwardRef, useReducer, useRef, memo, useCallback, useContext } from "react";
import { navigate } from "gatsby";
import PropTypes from "prop-types";
import { LuxeContext } from "@context/LuxeProvider";
import { useAuth } from "@context/AuthProvider";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import SlideOutMenu from "../SlideOutMenu";
import CategoryMenu from "./mobileMenu/CategoryMenu";
import BottomMenu from "./mobileMenu/BottomMenu";
import IconArrowLeft from "@svgs/arrow-left.svg";
import IconClose from "@svgs/close-delete.svg";
import IconAccount from "@svgs/account2025.svg";

function HeaderContent({ toggleSlideout, closeMenu }) {
  const { accountDetails, showLogin } = useAuth();
  const { luxe } = useContext(LuxeContext);
  const { subscriptionCredit, isLuxeActive } = luxe;

  const fullName = accountDetails ? `${accountDetails.firstName} ${accountDetails.lastName}` : "";

  const truncatedName = fullName.length > 15 ? `${fullName.slice(0, 15)}...` : fullName;

  const goToAccount = () => {
    navigate(isLuxeActive ? "/ana-luisa-luxe/" : "/account/");
    closeMenu();
  };

  return (
    <div className="sticky top-0 flex items-center w-full px-[15px] py-[18px] mb-[15px] border-b border-dark-grey bg-white h-[55px]">
      <IconAccount height="16" width="16" stroke="#000000" />
      {!accountDetails && (
        <button type="button" className="typo-body-bold underline ml-[10px]" onClick={showLogin}>
          Log in/ Sign up
        </button>
      )}
      {accountDetails && (
        <button
          type="button"
          className="flex items-center border-none background-transparent ml-[10px]"
          onClick={goToAccount}
        >
          <p className="typo-body-bold underline">{`Hi, ${truncatedName}`}</p>
          {subscriptionCredit > 0 && (
            <span className="ml-[10px] border border-black rounded-[4px] typo-micro font-bold px-[7px] py-[2px] mobile_menu_header_credit">
              ${Math.floor(subscriptionCredit)} credits
            </span>
          )}
        </button>
      )}

      <button
        type="button"
        className="absolute right-[15px]"
        onClick={toggleSlideout}
        aria-labelledby="close-button-label"
      >
        <span id="close-button-label" hidden>
          Close menu
        </span>
        <IconClose width={15} height={15} fill="none" stroke="#000000" />
      </button>
    </div>
  );
}

const initialState = {
  mainContentIsOpen: true,
  subContentIsOpen: false,
  categoryMenuContent: null,
  subSubContentIsOpen: false,
  subMenuContent: null,
  subMenuStatus: null,
  categoryMenuStatus: null,
  subMenuAlign: "right",
};

const MobileMenu = forwardRef(({ isOpen, toggleSlideout }, ref) => {
  const categoryMenuBackArrowRef = useRef(null);
  const subMenuBackArrowRef = useRef(null);
  // We are using a reducer here due to having to manage a lot of complex state
  // Components are difficult to debug if they have many state objects
  const reducer = (state, action) => {
    switch (action.type) {
      // Close one of the category menus (i.e. 'Trending' or 'Earrings')
      case "deactivateCategoryMenu":
        // Set all category statuses to 'false' to keep 'aria-expanded' attribute accurate (a11y)
        const newCategoryMenuStatus = {};
        for (const key in state.categoryMenuStatus) {
          newCategoryMenuStatus[key] = false;
        }
        // Wait for the sliding animation to finish before focusing on the Menu close button (a11y)
        if (ref && ref.current !== null) {
          setTimeout(() => ref.current.focus(), 300);
        }
        return {
          ...state,
          subMenuAlign: "right",
          subContentIsOpen: false,
          mainContentIsOpen: true,
          categoryMenuStatus: newCategoryMenuStatus,
        };
      // Close one of the sub-menus (.i.e 'Trending --> Gifts')
      case "deactivateSubMenu":
        // Set all sub-menu statuses to 'false' to keep 'aria-expanded' attribute accurate (a11y)
        const newSubMenuStatus = {};
        for (const key in state.subMenuStatus) {
          newSubMenuStatus[key] = false;
        }
        // Wait for the sliding animation to finish before focusing on the Category menu back arrow (a11y)
        if (categoryMenuBackArrowRef && categoryMenuBackArrowRef.current !== null) {
          setTimeout(() => categoryMenuBackArrowRef.current.focus(), 300);
        }
        return {
          ...state,
          subSubContentIsOpen: false,
          subContentIsOpen: true,
          subMenuStatus: newSubMenuStatus,
        };
      case "deactivateAllMenus":
        return initialState;
      // Open a category menu (i.e. 'Trending')
      case "activateCategoryMenu":
        // Wait for the sliding animation to finish before focusing on the Category menu back arrow (a11y)
        if (categoryMenuBackArrowRef && categoryMenuBackArrowRef.current !== null) {
          setTimeout(() => categoryMenuBackArrowRef.current.focus(), 300);
        }
        return {
          ...state,
          categoryMenuContent: action.payload.content,
          subContentIsOpen: true,
          mainContentIsOpen: false,
          categoryMenuStatus: { ...state.categoryMenuStatus, [action.payload.menuName]: true },
          subMenuAlign: "left",
        };
      // Open a sub-menu (i.e. 'Trending --> Gifts')
      case "activateSubMenu":
        // Wait for the sliding animation to finish before focusing on the sub-menu back arrow (a11y)
        if (subMenuBackArrowRef && subMenuBackArrowRef.current !== null) {
          setTimeout(() => subMenuBackArrowRef.current.focus(), 300);
        }
        return {
          ...state,
          subMenuContent: action.payload.content,
          subSubContentIsOpen: true,
          subContentIsOpen: false,
          subMenuStatus: { ...state.subMenuStatus, [action.payload.menuName]: true },
        };
      // Set the initial menu statuses all to false, so they can be updated when clicking a menu option
      // This allows 'aria-expanded' attributes for menus to be tracked for a11y purposes
      case "initializeMenuStatuses":
        return {
          ...state,
          categoryMenuStatus: action.payload.categoryMenuStatus,
          subMenuStatus: action.payload.subMenuStatus,
        };
      case "toggleMainContent":
        return {
          ...state,
          mainContentIsOpen: !state.mainContentIsOpen,
        };
      case "toggleSubContentIsOpen":
        return {
          ...state,
          subContentIsOPen: !state.subContentIsOpen,
        };
      default:
        throw new Error();
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const resetAndCloseMenu = useCallback(() => {
    dispatch({ type: "deactivateAllMenus" });
    toggleSlideout();
  }, [toggleSlideout]);

  let categoryMenuLabel = null;
  if ("categoryMenuStatus" in state && state.categoryMenuStatus != null) {
    categoryMenuLabel = Object.keys(state.categoryMenuStatus).map((e) => {
      if (state.categoryMenuStatus[e]) {
        return e;
      }
    });
  }
  const categoryHeaderContent = (
    <div className="flex justify-between items-center border-b border-dark-grey px-[15px] py-[18px] h-[55px]">
      <button
        type="button"
        onClick={() => dispatch({ type: "deactivateCategoryMenu" })}
        ref={categoryMenuBackArrowRef}
      >
        <IconArrowLeft height="16" stroke="#000000" fill="none" />
      </button>
      <span className="typo-h2-bold">{categoryMenuLabel}</span>
      <button type="button" onClick={resetAndCloseMenu} aria-labelledby="close-submenu-button">
        <IconClose width={15} height={15} fill="none" stroke="#000000" />
      </button>
    </div>
  );

  let subCategoryMenuLabel = null;
  if ("subMenuStatus" in state && state.subMenuStatus != null) {
    subCategoryMenuLabel = Object.keys(state.subMenuStatus).map((e) => {
      if (state.subMenuStatus[e]) {
        return e;
      }
    });
  }
  const subMenuHeaderContent = (
    <div className="flex justify-between items-center border-b border-dark-grey px-[15px] py-[18px] mb-[15px] h-[55px]">
      <button
        type="button"
        onClick={() => dispatch({ type: "deactivateSubMenu" })}
        ref={categoryMenuBackArrowRef}
      >
        <IconArrowLeft width="25" stroke="#000000" fill="none" />
      </button>
      <span className="h2 h2--bold">{subCategoryMenuLabel}</span>
      <button type="button" onClick={resetAndCloseMenu} aria-labelledby="close-submenu-button">
        <IconClose width={15} height={15} fill="none" stroke="#000000" />
      </button>
    </div>
  );
  return (
    <SlideOutMenu align="left" isSlideoutOpen={isOpen}>
      <SlideOutMenu
        align="left"
        isSlideoutOpen={state.mainContentIsOpen}
        headerContent={
          <HeaderContent toggleSlideout={toggleSlideout} closeMenu={resetAndCloseMenu} />
        }
      >
        <CategoryMenu
          initializeMenuStatuses={(categoryMenuStatus) =>
            dispatch({
              type: "initializeMenuStatuses",
              payload: { categoryMenuStatus },
            })
          }
          activateCategoryMenu={(content, menuName) =>
            dispatch({ type: "activateCategoryMenu", payload: { content, menuName } })
          }
          categoryMenuStatus={state.categoryMenuStatus}
          closeMenu={resetAndCloseMenu}
        />
        <BottomMenu closeMenu={resetAndCloseMenu} />
      </SlideOutMenu>
      <SlideOutMenu
        align={state.subMenuAlign}
        isSlideoutOpen={state.subContentIsOpen}
        headerContent={categoryHeaderContent}
      >
        {state.categoryMenuContent}
      </SlideOutMenu>
      <SlideOutMenu
        align="right"
        isSlideoutOpen={state.subSubContentIsOpen}
        headerContent={subMenuHeaderContent}
      >
        {state.subMenuContent}
      </SlideOutMenu>
    </SlideOutMenu>
  );
});

MobileMenu.displayName = "MobileMenu";

MobileMenu.propTypes = {
  isOpen: PropTypes.bool,
  toggleSlideout: PropTypes.func,
};

export default memo(
  withALErrorBoundary({
    name: "MobileMenu",
    priority: "P2",
  })(MobileMenu)
);
